import {
    PRODUCT_ID_CITY_BREAK_TICKET_ADULT,
    PRODUCT_ID_CITY_BREAK_TICKET_TEENAGER,
    PRODUCT_TYPE_TICKET,
    PRODUCT_TYPE_VOUCHER,
} from 'constants/app'
import { CITY_BREAK_TICKET_PRICE } from 'constants/prices'
import { formatPrice } from 'helpers/helpers'
import moment from 'moment'
import {
    CartItemTicketDetailDto,
    CartItemTransportTicketDetailDto,
    GetCartItemPresenter,
} from 'store/api-slice/hooks'
import {
    BusTicketWithDetails,
    CabanaWithDetails,
    Card,
    Ticket,
    TicketsBundle,
} from 'store/api-slice/types'
import { LanguageType } from 'store/steps'

export const busTicketsSorting = (busTickets: BusTicketWithDetails[]) =>
    busTickets.slice().sort((ticketOne, ticketTwo) => {
        const formattedDateOne = new Date(
            `${ticketOne.detail.date}, ${ticketOne.detail.time}`
        ).getTime()
        const formattedDateTwo = new Date(
            `${ticketTwo.detail.date}, ${ticketTwo.detail.time}`
        ).getTime()

        return formattedDateOne - formattedDateTwo
    })

export const cabanasSorting = (cabanas: CabanaWithDetails[]) =>
    cabanas.slice().sort((cabanaFirst, cabanaSecond) => {
        const digitalDateFirst = new Date(
            `${cabanaFirst.detail.date}`
        ).getTime()
        const digitalDateSecond = new Date(
            `${cabanaSecond.detail.date}`
        ).getTime()

        return digitalDateFirst - digitalDateSecond
    })

export const sumTicketsDates = (tickets: Ticket[]) => {
    const datesArr = tickets.map((ticket) => ticket.detail.date).flat()
    return Array.from(
        new Set(
            datesArr.sort((dateOne, dateTwo) =>
                moment(dateOne).diff(moment(dateTwo))
            )
        )
    )
}

export const sumTicketsBundlesDates = (
    ticketsBundles: GetCartItemPresenter[]
) => {
    const datesFromBundledTickets: string[] = ticketsBundles.map(
        ({ itemsBundle }) => {
            const sampleTicketFromBundle = itemsBundle?.find(
                ({ productType }) => productType === PRODUCT_TYPE_TICKET
            )

            if (!sampleTicketFromBundle?.detail) return ''

            return (
                sampleTicketFromBundle?.detail as CartItemTransportTicketDetailDto
            ).date
        }
    )
    const sortedDates = datesFromBundledTickets.sort((dateOne, dateTwo) =>
        moment(dateOne).diff(moment(dateTwo))
    )
    // eliminate repeated dates
    const reducedDates = Array.from(new Set(sortedDates))

    return reducedDates
}

export const mergeTickets = (tickets: Ticket[]): GetCartItemPresenter[] => {
    const boxIds = Array.from(
        new Set(tickets.map((ticket) => ticket.detail.boxId))
    )
    const sortedTicketsByBoxId = boxIds.map((boxId) => {
        const filteredTickets = tickets.filter(
            (ticket) => ticket.detail.boxId === boxId
        )

        return {
            name: filteredTickets[0].name,
            description: filteredTickets[0].description,
            key: `${filteredTickets[0].detail.person}-${filteredTickets[0].detail.zone}-${filteredTickets[0].detail.ticketTime}`,
            price: filteredTickets
                .map((ticket) => ticket.unitPrice)
                .reduce((prev, next) => (prev || 0) + (next || 0)),
            hasPromotion: filteredTickets.some((ticket) => ticket.hasPromotion),
            lowestPriceLast30Days: filteredTickets
                .map((ticket) =>
                    ticket.lowestPriceLast30Days !== null
                        ? ticket.lowestPriceLast30Days
                        : ticket.totalPriceWithoutDiscount
                )
                .reduce((prev, next) => (prev || 0) + (next || 0)),
            totalPriceWithoutDiscount: filteredTickets
                .map((ticket) => ticket.totalPriceWithoutDiscount)
                .reduce((prev, next) => (prev || 0) + (next || 0)),
            totalPrice: filteredTickets
                .map((ticket) => ticket.totalPrice)
                .reduce((prev, next) => (prev || 0) + (next || 0)),
            detail: filteredTickets[0].detail,
            cartItemId: filteredTickets[0].cartItemId,
        }
    })

    const keys = Array.from(
        new Set(sortedTicketsByBoxId.map((ticket) => ticket.key))
    )

    const mergeTickets: {
        name?: string
        description?: string
        unitPrice?: number
        quantity?: number
        lowestPriceLast30Days?: number | null
        hasPromotion?: boolean
        totalPriceWithoutDiscount?: number | null
        totalPrice?: number
        cartItemId?: string
        detail: CartItemTicketDetailDto
    }[] = []

    keys.map((key) => {
        const filteredTickets = sortedTicketsByBoxId.filter(
            (ticket) => ticket.key === key
        )

        if (filteredTickets.length) {
            const filteredTicketsWithDiscount = filteredTickets.filter(
                (filteredTicket) => filteredTicket.hasPromotion
            )

            const filteredTicketsWithoutDiscount = filteredTickets.filter(
                (filteredTicket) => !filteredTicket.hasPromotion
            )

            if (filteredTicketsWithDiscount.length) {
                const priceKeys = Array.from(
                    new Set(
                        filteredTicketsWithDiscount.map(
                            (ticket) => ticket.totalPrice
                        )
                    )
                )

                priceKeys.map((key) => {
                    const filteredTicketsByPrice =
                        filteredTicketsWithDiscount.filter(
                            (ticket) => ticket.totalPrice === key
                        )

                    if (filteredTicketsByPrice.length) {
                        mergeTickets.push({
                            name: filteredTicketsByPrice[0].name,
                            description: filteredTicketsByPrice[0].description,
                            unitPrice: filteredTicketsByPrice[0].price,
                            quantity: filteredTicketsByPrice.length,
                            lowestPriceLast30Days:
                                filteredTicketsByPrice[0].lowestPriceLast30Days,
                            hasPromotion:
                                filteredTicketsByPrice[0].hasPromotion,
                            totalPriceWithoutDiscount:
                                filteredTicketsByPrice[0]
                                    .totalPriceWithoutDiscount,
                            totalPrice: filteredTicketsByPrice[0].totalPrice,
                            cartItemId: filteredTicketsByPrice[0].cartItemId,
                            detail: filteredTickets[0].detail,
                        })
                    }

                    return {}
                })
            }

            filteredTicketsWithoutDiscount.length &&
                mergeTickets.push({
                    name: filteredTicketsWithoutDiscount[0].name,
                    description: filteredTicketsWithoutDiscount[0].description,
                    unitPrice: filteredTicketsWithoutDiscount[0].price,
                    quantity: filteredTicketsWithoutDiscount.length,
                    lowestPriceLast30Days:
                        filteredTicketsWithoutDiscount[0].lowestPriceLast30Days,
                    hasPromotion:
                        filteredTicketsWithoutDiscount[0].hasPromotion,
                    totalPriceWithoutDiscount:
                        filteredTicketsWithoutDiscount[0]
                            .totalPriceWithoutDiscount,
                    totalPrice: filteredTicketsWithoutDiscount[0].totalPrice,
                    cartItemId: filteredTicketsWithoutDiscount[0].cartItemId,
                    detail: filteredTickets[0].detail,
                })
        }

        return {}
    })

    return mergeTickets
}

interface IMergeTicketsBundles {
    ticketsBundles: TicketsBundle[]
    language: LanguageType
}

export const mergeTicketsBundles = ({
    ticketsBundles,
    language,
}: IMergeTicketsBundles): GetCartItemPresenter[] => {
    const currency = language === 'pl' ? 'zł' : 'PLN'

    // collect all box ids
    const boxIdsFromBundledTickets = ticketsBundles.map(({ itemsBundle }) => {
        const sampleTicketFromBundle = itemsBundle?.find(
            ({ productType }) => productType === PRODUCT_TYPE_TICKET
        )

        if (!sampleTicketFromBundle?.detail) return undefined

        return (
            sampleTicketFromBundle?.detail as CartItemTransportTicketDetailDto
        ).boxId
    })
    const reducedBoxIds = Array.from(new Set(boxIdsFromBundledTickets))

    // merge all bundles with the same box ids
    const bundlesReducedByBoxId: GetCartItemPresenter[] = reducedBoxIds.map(
        (boxId) => {
            const bundlesWithCurrentBoxId = ticketsBundles.filter(
                ({ itemsBundle }) => {
                    const sampleTicketFromBundle = itemsBundle?.find(
                        ({ productType }) => productType === PRODUCT_TYPE_TICKET
                    )

                    if (!sampleTicketFromBundle?.detail) return false

                    return (
                        (
                            sampleTicketFromBundle?.detail as CartItemTransportTicketDetailDto
                        ).boxId === boxId
                    )
                }
            )

            if (!bundlesWithCurrentBoxId.length) return {}

            const {
                itemsBundle: sampleItemsBundle,
                name: sampleName,
                cartItemId: sampleCartItemId,
            } = bundlesWithCurrentBoxId[0]

            // merge descriptions of items from single bundle
            const itemsDescriptions = sampleItemsBundle?.map(
                ({ productType, productId, name, unitPrice }) => {
                    const itemsWithSimilarProductId = sampleItemsBundle?.filter(
                        ({ productId: similarProductId }) =>
                            similarProductId === productId
                    )

                    // description for voucher
                    if (productType === PRODUCT_TYPE_VOUCHER)
                        return `${
                            itemsWithSimilarProductId.length
                        }x Voucher ${formatPrice(unitPrice || 0)} ${currency}`
                    // description for tickets
                    return `${itemsWithSimilarProductId.length}x ${name || ''}`
                }
            )
            const reducedDescriptions = Array.from(new Set(itemsDescriptions))
            const sortedDescriptions = reducedDescriptions.sort(
                (txtA, txtB) => {
                    const trimmedTxtA = txtA.replace(/\dx/, '')
                    const trimmedTxtB = txtB.replace(/\dx/, '')

                    if (trimmedTxtA < trimmedTxtB) return -1
                    if (trimmedTxtA > trimmedTxtB) return 1
                    return 0
                }
            )
            const formattedDescriptions = sortedDescriptions.join('<br>')

            // sum all similar bundles from the selected dates range
            const reducedTotalPrice = bundlesWithCurrentBoxId
                .map((bundle) => bundle.totalPrice || 0)
                .reduce((prev, next) => prev + next, 0)
            const reducedUnitPrice = bundlesWithCurrentBoxId
                .map((bundle) => bundle.unitPrice || 0)
                .reduce((prev, next) => prev + next, 0)
            const reducedTotalPriceWithoutDiscount = bundlesWithCurrentBoxId
                .map((bundle) => bundle.totalPriceWithoutDiscount || 0)
                .reduce((prev, next) => prev + next, 0)
            const reducedLowestPriceLast30Days = bundlesWithCurrentBoxId
                .map((bundle) => bundle.totalPriceWithoutDiscount || 0)
                .reduce((prev, next) => prev + next, 0)

            const hasPromotionReduced = bundlesWithCurrentBoxId.some(
                (bundle) => !!bundle.hasPromotion
            )

            return {
                quantity: 1,
                name: sampleName,
                description: formattedDescriptions,
                unitPrice: reducedUnitPrice,
                totalPrice: reducedTotalPrice,
                totalPriceWithoutDiscount: reducedTotalPriceWithoutDiscount,
                lowestPriceLast30Days:
                    reducedLowestPriceLast30Days ||
                    reducedTotalPriceWithoutDiscount,
                hasPromotion: hasPromotionReduced,
                cartItemId: sampleCartItemId,
                itemsBundle: [],
                detail: {
                    person: 'adult',
                    zone: 'jamango',
                    ticketTime: 'day',
                } as CartItemTicketDetailDto,
            }
        }
    )

    return bundlesReducedByBoxId
}

export const mergeCityBreakTickets = (
    tickets: Ticket[]
): GetCartItemPresenter[] => {
    // TODO: move into helper (used also in city cart hook)
    const ticketsDates = tickets.map(({ detail: { date } }) => date)
    const datesRange: string[] = ticketsDates
        .filter((date, index) => ticketsDates.indexOf(date) === index)
        .sort()

    const daysNumber = datesRange.length

    const adultTickets = tickets.filter(
        ({ productId }) => productId === PRODUCT_ID_CITY_BREAK_TICKET_ADULT
    )
    const teenageTickets = tickets.filter(
        ({ productId }) => productId === PRODUCT_ID_CITY_BREAK_TICKET_TEENAGER
    )
    const adultReducedTicket: GetCartItemPresenter[] =
        adultTickets.length > 0
            ? [
                  {
                      ...adultTickets[0],
                      quantity: adultTickets.length / daysNumber,
                      unitPrice: CITY_BREAK_TICKET_PRICE * daysNumber,
                  },
              ]
            : []
    const teenageReducedTicket: GetCartItemPresenter[] =
        teenageTickets.length > 0
            ? [
                  {
                      ...teenageTickets[0],
                      quantity: teenageTickets.length / daysNumber,
                      unitPrice: CITY_BREAK_TICKET_PRICE * daysNumber,
                  },
              ]
            : []

    return [...adultReducedTicket, ...teenageReducedTicket]
}

interface MergedCardsWithPromotions {
    name?: string
    description?: string
    unitPrice?: number
    quantity?: number
    lowestPriceLast30Days?: number | null
    hasPromotion?: boolean
    totalPriceWithoutDiscount?: number | null
    totalPrice?: number
    cartItemId?: string
}

export const mergeCardsWithPromotions = (
    cards: Card[]
): MergedCardsWithPromotions[] => {
    const cardsWithPromotions = cards.filter(({ hasPromotion }) => hasPromotion)

    if (!cardsWithPromotions.length) return []

    const productIdsWithPromotions = Array.from(
        new Set(cardsWithPromotions.map((card) => card.productId))
    )
    const cardsSortedByProductId: MergedCardsWithPromotions[] = []

    productIdsWithPromotions.forEach((productId) => {
        const cardsMatchingProductId = cardsWithPromotions.filter(
            ({ productId: productIdItem }) => productId === productIdItem
        )

        if (!cardsMatchingProductId.length) return

        const {
            name,
            description,
            unitPrice,
            lowestPriceLast30Days,
            hasPromotion,
            totalPriceWithoutDiscount,
            totalPrice,
            cartItemId,
        } = cardsMatchingProductId[0]

        cardsSortedByProductId.push({
            name,
            description,
            unitPrice,
            quantity: cardsMatchingProductId.length,
            lowestPriceLast30Days,
            hasPromotion,
            totalPriceWithoutDiscount,
            totalPrice,
            cartItemId,
        })
    })

    return cardsSortedByProductId
}
